import * as React from 'react';
import { cn } from '@/utils/cn';

// ─── Table Shell ─────────────────────────────────────────────────────────────

export const Table = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement>
>(({ className, ...props }, ref) => (
  <div className="relative w-full overflow-auto">
    <table
      ref={ref}
      className={cn('w-full caption-bottom text-sm border-collapse', className)}
      {...props}
    />
  </div>
));
Table.displayName = 'Table';

// ─── Header ──────────────────────────────────────────────────────────────────

export const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    className={cn(
      'bg-white dark:bg-slate-900 text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest border-b border-slate-100 dark:border-slate-800',
      className
    )}
    {...props}
  />
));
TableHeader.displayName = 'TableHeader';

// ─── Body ─────────────────────────────────────────────────────────────────────

export const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn('divide-y divide-slate-100/60 dark:divide-slate-800/60', className)}
    {...props}
  />
));
TableBody.displayName = 'TableBody';

// ─── Footer ──────────────────────────────────────────────────────────────────

export const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      'border-t border-slate-100 dark:border-slate-800 bg-slate-50/30 dark:bg-slate-800/20 font-medium',
      className
    )}
    {...props}
  />
));
TableFooter.displayName = 'TableFooter';

// ─── Row ──────────────────────────────────────────────────────────────────────

export const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      'group hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors',
      className
    )}
    {...props}
  />
));
TableRow.displayName = 'TableRow';

// ─── Head Cell ────────────────────────────────────────────────────────────────

export const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn('px-6 py-3 whitespace-nowrap text-left', className)}
    {...props}
  />
));
TableHead.displayName = 'TableHead';

// ─── Body Cell ────────────────────────────────────────────────────────────────

export const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn('px-6 py-3 align-middle', className)}
    {...props}
  />
));
TableCell.displayName = 'TableCell';

// ─── Caption ──────────────────────────────────────────────────────────────────

export const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn('mt-4 text-sm text-slate-500 dark:text-slate-400', className)}
    {...props}
  />
));
TableCaption.displayName = 'TableCaption';